import { useState, useCallback, useMemo } from "react";
import { Box, Typography, Snackbar, Alert, useMediaQuery } from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { useNavigate } from "react-router-dom";

import {
  EXPLORE_CUISINE_ITEMS,
  EXPLORE_PRICE_ITEMS,
  HOME_DEFAULT_CUISINE_ID,
  HOME_DEFAULT_PRICE_ID,
} from "../components/deals/ExploreDealsPanel/constants";
import HeroCuisineScroll from "../components/home/HeroCuisineScroll";
import HeroPriceScroll from "../components/home/HeroPriceScroll";
import CustomButton from "../components/ui/CustomButton";
import LogoCarousel from "../components/ui/LogoCarousel";
import {
  HOME_SECTION_BAND_PADDING_Y,
  HOME_SECTION_HEADING_BELOW_MARGIN,
  HOME_SECTION_HEADING_SX,
  HOME_SECTION_HORIZONTAL_GUTTER,
  HOME_SECTION_HERO_PADDING_TOP,
} from "../constants/homePageSections";

const HERO_WHEEL_VISIBLE_ROWS = 4;
const HERO_CUISINE_ROW_HEIGHT_PX = 76;

const PICKERS_STRIP_MAX_PX = 640;

/** Same value for outer strip lateral padding ↔ gap between deal-kind & price wheels. */
const PICKERS_STRIP_INLINE_GUTTER = { xs: 3, md: 4 };

const Home = () => {
  const navigate = useNavigate();

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage] = useState("");
  const [snackbarSeverity] = useState("success");
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [cuisineId, setCuisineId] = useState(HOME_DEFAULT_CUISINE_ID);
  const [priceId, setPriceId] = useState(HOME_DEFAULT_PRICE_ID);

  const cuisineIndex = useMemo(
    () =>
      Math.max(
        0,
        EXPLORE_CUISINE_ITEMS.findIndex((item) => item.id === cuisineId),
      ),
    [cuisineId],
  );

  const priceIndex = useMemo(
    () =>
      Math.max(
        0,
        EXPLORE_PRICE_ITEMS.findIndex((item) => item.id === priceId),
      ),
    [priceId],
  );

  const handleGenerate = useCallback(() => {
    const qs = new URLSearchParams();
    qs.set("date", "today");
    const picked = EXPLORE_CUISINE_ITEMS.find((i) => i.id === cuisineId);
    const price = EXPLORE_PRICE_ITEMS.find((i) => i.id === priceId);
    if (picked?.category) qs.set("category", picked.category);
    else if (picked?.cuisine) qs.set("category", picked.cuisine);
    if (price?.band && price.band !== "any") qs.set("price_band", price.band);
    if (price?.discountType) qs.set("discount_type", price.discountType);
    navigate(`/deals?${qs.toString()}`);
  }, [cuisineId, priceId, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        overflowX: "hidden",
      }}
    >
      <Box
        component="section"
        aria-labelledby="home-pickers-heading"
        sx={{
          width: "100%",
          maxWidth: 1040,
          mx: "auto",
          px: HOME_SECTION_HORIZONTAL_GUTTER,
          pt: HOME_SECTION_HERO_PADDING_TOP,
          pb: HOME_SECTION_BAND_PADDING_Y,
          minHeight: { xs: "min-content", md: "clamp(440px, 72vh, 820px)" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: PICKERS_STRIP_MAX_PX,
            px: PICKERS_STRIP_INLINE_GUTTER,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            boxSizing: "border-box",
          }}
        >
          <Typography
            component="h1"
            id="home-pickers-heading"
            sx={{
              ...HOME_SECTION_HEADING_SX,
              ...HOME_SECTION_HEADING_BELOW_MARGIN,
            }}
          >
            Find the best food deals {!isMobile && <br />} near you 🇲🇾
          </Typography>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              direction: "ltr",
              alignItems: "stretch",
              gap: PICKERS_STRIP_INLINE_GUTTER,
              mt: 2,
              justifyContent: "center",
            }}
          >
            <Box sx={{ flex: "1 1 0%", minWidth: 0 }}>
              <HeroCuisineScroll
                items={EXPLORE_CUISINE_ITEMS}
                selectedIndex={cuisineIndex}
                onSelectedIndexChange={(idx) =>
                  setCuisineId(EXPLORE_CUISINE_ITEMS[idx].id)
                }
                ariaLabel="Choose category"
                rowHeightPx={HERO_CUISINE_ROW_HEIGHT_PX}
                visibleRows={HERO_WHEEL_VISIBLE_ROWS}
              />
            </Box>

            <Box sx={{ flex: "1 1 0%", minWidth: 0 }}>
              <HeroPriceScroll
                items={EXPLORE_PRICE_ITEMS}
                selectedIndex={priceIndex}
                onSelectedIndexChange={(idx) =>
                  setPriceId(EXPLORE_PRICE_ITEMS[idx].id)
                }
                ariaLabel="Choose price band"
                rowHeightPx={HERO_CUISINE_ROW_HEIGHT_PX}
                visibleRows={HERO_WHEEL_VISIBLE_ROWS}
              />
            </Box>
          </Box>

          <CustomButton
            sx={{
              alignSelf: "center",
              mt: { xs: 5, md: 6 },
              py: 1.25,
            }}
            variant="primary"
            onClick={handleGenerate}
          >
            Show me deals
          </CustomButton>
        </Box>
      </Box>

      <Box
        component="section"
        aria-label="Mission"
        sx={{
          width: "100%",
          maxWidth: 1040,
          mx: "auto",
          px: HOME_SECTION_HORIZONTAL_GUTTER,
          py: HOME_SECTION_BAND_PADDING_Y,
          display: "flex",
          flexDirection: "column",
          minHeight: { xs: "60vh", md: "85vh" },
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Typography
          component="h2"
          sx={{
            ...HOME_SECTION_HEADING_SX,
            ...HOME_SECTION_HEADING_BELOW_MARGIN,
            mb: 0,
          }}
        >
          Loops finds the best deals in Malaysia for you, {!isMobile && <br />}{" "}
          all in one place.
        </Typography>

        <CustomButton
          variant="secondary"
          endIcon={<NorthEastIcon sx={{ fontSize: "1.125rem" }} />}
          onClick={() => navigate("/deals")}
          sx={{
            px: 3,
            py: 1.5,
          }}
        >
          Browse Deals
        </CustomButton>
      </Box>
      {/* Logo Carousel Section */}
      <LogoCarousel />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Home;
